import { computed } from "vue";
import { useStore } from "vuex";
import {
  IBienBanGiaiTheResponseBase,
  IHopDongResponseBase,
} from "../interfaces/ApiResponses";

const OPT_BBGT_REQUIRED_APPROVAL = "OPT_BBGT_REQUIRED_APPROVAL";

function parseApprovalLevel(value: unknown): number {
  if (value === undefined || value === null) {
    return 0;
  }

  return parseInt(value as string, 10) || 0;
}

function isTruthyFlag(value: unknown): boolean {
  return value === true || value === 1 || value === "1";
}

export function shouldShowConfirmButtonForLevel(
  bienBan: IBienBanGiaiTheResponseBase | null | undefined,
  level: number
): boolean {
  if (!bienBan || bienBan.ngay_ra_xuong) {
    return false;
  }

  if (level !== 2) {
    return false;
  }

  return !isTruthyFlag(bienBan.is_approved);
}

export function shouldShowApproveButtonForLevel(
  bienBan: IBienBanGiaiTheResponseBase | null | undefined,
  level: number
): boolean {
  if (!bienBan || bienBan.ngay_ra_xuong) {
    return false;
  }

  if (level !== 1 && level !== 2) {
    return false;
  }

  if (isTruthyFlag(bienBan.is_admin_approved)) {
    return false;
  }

  if (level === 2 && !isTruthyFlag(bienBan.is_approved)) {
    return false;
  }

  return true;
}

export const useBienBan = () => {
  const store = useStore();

  /**
   * Working-unit BBGT approval level from GET /me/abilities options.
   * 0 = none, 1 = approve only, 2 = confirm + approve
   */
  const approvalLevel = computed(() => {
    try {
      if (!store) {
        return 0;
      }

      const level = store.getters.workingUnitBBGTApprovalLevel;
      if (typeof level === "number") {
        return level;
      }

      const getDonViOption =
        store.getters.getDonViOption ||
        store.getters["AuthzModule/getDonViOption"];
      if (typeof getDonViOption === "function") {
        return parseApprovalLevel(getDonViOption(OPT_BBGT_REQUIRED_APPROVAL));
      }

      const authzOptions = store.state?.AuthzModule?.options as
        | Record<string, string>
        | undefined;
      if (authzOptions) {
        return parseApprovalLevel(authzOptions[OPT_BBGT_REQUIRED_APPROVAL]);
      }

      return 0;
    } catch (error) {
      console.warn("Error getting BBGT approval level:", error);
      return 0;
    }
  });

  const shouldShowConfirmButton = (
    bienBan: IBienBanGiaiTheResponseBase | null | undefined
  ): boolean => {
    return shouldShowConfirmButtonForLevel(bienBan, approvalLevel.value);
  };

  const shouldShowApproveButton = (
    bienBan: IBienBanGiaiTheResponseBase | null | undefined
  ): boolean => {
    return shouldShowApproveButtonForLevel(bienBan, approvalLevel.value);
  };

  const calculateSoLuongVatTuCan = ({
    bien_ban_kiem_nghiem: bienBanItems,
    hop_dong_vat_tu: hopDongVatTuItems,
  }: IHopDongResponseBase) => {
    const result: number[] = [];
    hopDongVatTuItems.forEach(
      ({ id: idVatTuHopDong, pivot: { so_luong: soLuongTongCong } }) => {
        let soLuongDaCo = 0;

        bienBanItems.forEach((bienBanItem) => {
          const vatTuBienBanItem = bienBanItem.vat_tu_bien_ban_kiem_nghiem.find(
            (vatTuBienBan) => vatTuBienBan.id === idVatTuHopDong
          );

          if (vatTuBienBanItem) {
            const soLuongTrongBienBan = +vatTuBienBanItem.pivot.so_luong || 0;

            soLuongDaCo += soLuongTrongBienBan;
          }
        });

        if (!isNaN(+soLuongTongCong - +soLuongDaCo)) {
          result.push(+soLuongTongCong - +soLuongDaCo);
        }
      }
    );

    return result;
  };

  return {
    calculateSoLuongVatTuCan,
    approvalLevel,
    shouldShowConfirmButton,
    shouldShowApproveButton,
  };
};
